/*
 * PROD-7828 R5b — pure adapters: goserver "Needs attention" payloads (the contract of record) -> view
 * models. The JSX renders only what these return. adapters.test.js replays the goserver-generated
 * goldens in __fixtures__/ and deep-equals the exported key lists against needs_attention_enums.json
 * (generated from the Go registry), so a wire-shape drift fails here instead of on screen.
 */
import { api_error, fmt, fmt_count, month_label, num } from '../format';
import { NEEDS_REVIEW_TEXT, short_uuid } from '../overview/adapters';
import { type_label } from '../penalties/params';
import { ist_day } from '../shared/ist';
import {
  ACTION_ACKNOWLEDGE,
  MAX_TEACHER_SEARCH_LEN,
  RECENT_DECISIONS_LIMIT,
  TIER_LABELS,
  is_acknowledgeable,
  is_blocker_overridable,
  is_issue_live,
  is_no_month_row,
  is_outcome_unknown,
  is_selectable_for_close,
  parse_link_target,
  has_no_stake
} from './params';

// A stake_rule NONE issue's amount_at_stake is always 0; "—" reads as "no pay impact", never as a
// computed ₹0 (which stays ₹0, a real number Ops should see).
export const NO_STAKE_TEXT = '—';
const stake_text = (amount, issue_code) => (has_no_stake(issue_code) ? NO_STAKE_TEXT : fmt(amount));

const text = (v, fallback = '—') => {
  const s = v === null || v === undefined ? '' : String(v);
  return s === '' ? fallback : s;
};

const list_of = (v) => (Array.isArray(v) ? v : []);

// ---- the wire key lists (the R5 F7 rule). Deep-equalled against needs_attention_enums.json. ----

export const TIER_ROW_KEYS = [
  'tier',
  'label',
  'blocks_close',
  'open_count',
  'acknowledged_count',
  'resolved_count',
  'auto_cleared_count',
  'teachers',
  'sessions',
  'amount_at_stake'
];
export const READINESS_KEYS = [
  'ready',
  'headline',
  'errors_open',
  'must_review_open',
  'blocking_teachers',
  'teachers_total',
  'teachers_closed',
  'teachers_open',
  'teachers_ready',
  'requires_override',
  'degraded'
];
export const ISSUE_ROW_KEYS = [
  'id',
  'issue_code',
  'tier',
  'label',
  'flag_reason',
  'suggested_action',
  'blocks_close',
  'status',
  'teacher_uuid',
  'teacher_name',
  'teacher_slug',
  'pay_month',
  'session_id',
  'session_uuid',
  'session_start_ist',
  'student_name',
  'amount_at_stake',
  'first_seen_at',
  'last_seen_at',
  'resolved_at',
  'resolved_by',
  'resolved_by_name',
  'resolution_note',
  'acknowledgeable'
];
export const TEACHER_READINESS_KEYS = [
  'teacher_uuid',
  'teacher_name',
  'lifecycle',
  'rounded_net_payable',
  'payable_amount',
  'needs_review',
  'closable',
  'blockers',
  'session_count'
];
export const BLOCKER_KEYS = ['code', 'message', 'overridable', 'how_to_fix'];
export const CLOSE_RESULT_KEYS = [
  'teacher_uuid',
  'teacher_name',
  'outcome',
  'lifecycle',
  'grant_written',
  'rounded_net_payable',
  'payable_amount',
  'needs_review',
  'override_used',
  'blockers',
  'grant_mismatch'
];
export const GRANT_MISMATCH_KEYS = [
  'session_uuid',
  'penalty_uuid',
  'day_ist',
  'penalty_type',
  'amount',
  'is_overridden',
  'how_to_fix'
];
export const DAILY_LINE_KEYS = [
  'issue_code',
  'tier',
  'label',
  'count',
  'teachers',
  'amount_at_stake',
  'pay_months'
];
export const DAILY_REPORT_KEYS = [
  'day',
  'from',
  'to',
  'new_issues',
  'cleared_issues',
  'threshold_crossings',
  'job_events',
  'totals',
  'all_clear',
  'monthly_link'
];
export const ISSUE_ACTION_RESULT_KEYS = ['issue_id', 'applied', 'status', 'code', 'message'];
// Response-level keys in wire order (contract §13.1/§13.2): close_enabled is the last field of both.
export const SUMMARY_KEYS = [
  'pay_month',
  'tiers',
  'readiness',
  'generated_at',
  'shadow_banner',
  'recent_decisions',
  'decisions_total',
  'close_enabled'
];
export const READINESS_LIST_KEYS = [
  'pay_month',
  'state',
  'rows',
  'count',
  'total_count',
  'limit',
  'offset',
  'readiness',
  'close_enabled'
];
export const OPS_DECISION_KEYS = [
  'issue_id',
  'issue_code',
  'tier',
  'label',
  'action',
  'resolved_by',
  'resolved_by_name',
  'resolved_at',
  'resolution_note',
  'teacher_uuid',
  'teacher_name'
];

// Every 4xx of this feature carries a stable error_code, so the page branches on the code and never on
// prose (CLOSE_DISABLED in particular must read as "the switch is off", not as a failure).
export const ERROR_CODES = [
  'BAD_PAY_MONTH',
  'BAD_TIER',
  'BAD_STATUS',
  'BAD_ISSUE_CODE',
  'BAD_TEACHER_UUID',
  'BAD_PAGINATION',
  'BAD_SORT',
  'BAD_DAY',
  'BAD_ACTION',
  'BAD_STATE',
  'BAD_BODY',
  'BAD_QUERY',
  'REASON_REQUIRED',
  'REASON_TOO_LONG',
  'TEACHER_NOT_FOUND',
  'TOO_MANY_IDS',
  'TOO_MANY_TEACHERS',
  'FORBIDDEN',
  'LOOKUP_FAILED',
  'CLOSE_DISABLED',
  'CLOSE_SWITCH_CHANGED'
];

export const CLOSE_DISABLED_TEXT =
  'Month close is switched off. A Teacher Payment super user can turn it on from the Month close chip at the top of the page.';

// CD-124. Month order reads earlier/later (HM1), so the deficit lands in the "following" month.
export const CARRY_FORWARD_TEXT =
  'A negative month carries its deficit into the following month as a system deduction; reopening reverses it.';

export const is_close_disabled = (e) => api_error(e).code === 'CLOSE_DISABLED';

// These codes carry a developer-facing reason ("invalid request body"); Ops read this instead (CD-80).
export const ERROR_CODE_TEXT = {
  BAD_BODY:
    'The server could not read this request, so it changed nothing. Reload the page and try again; if it happens again, ask engineering.',
  BAD_QUERY: `The teacher search is too long. Search with at most ${MAX_TEACHER_SEARCH_LEN} characters of a name or email.`
};

export const error_code_text = (e) => ERROR_CODE_TEXT[api_error(e).code] || '';

const http_status = (e) => {
  try {
    return Number(e.response.status) || 0;
  } catch (err) {
    return 0;
  }
};

/*
 * How a failed write (close, reopen, issue action) ended. 'dropped' — no answer (timeout, network) or
 * a 5xx: it may have been partly processed server-side. A 4xx was refused before anything was written.
 */
export const close_failure_kind = (e) => {
  const status = http_status(e);
  if (status === 0 || status >= 500) return 'dropped';
  if (is_close_disabled(e)) return 'disabled';
  if (api_error(e).code === 'REASON_TOO_LONG') return 'reason_too_long';
  return 'refused';
};

/*
 * Close and reopen only: goserver answers their 500 LOOKUP_FAILED before any write, and a
 * COMMIT-uncertain failure as *_OUTCOME_UNKNOWN, so that sentence is a refusal. A proxy 5xx carries no
 * code and stays a drop. An issue action's LOOKUP_FAILED may have saved (CD-161), so it is not here.
 */
export const month_write_failure_kind = (e) => {
  const { code, message } = api_error(e);
  if (http_status(e) >= 500 && code === 'LOOKUP_FAILED' && message) return 'refused';
  return close_failure_kind(e);
};

// D74(a): the month summary is a replica read, so it can briefly show a just-closed teacher as open.
// Deliberately carries no number.
export const CLOSE_SETTLE_NOTE =
  'The teacher list and month summary can take a moment to show this close. Refresh if a teacher you just closed still shows as open.';

// CD-99: the server counts a not-confirmed close as refused; only the result rows say which ones.
const not_confirmed_count = (results) =>
  list_of(results).filter((r) => !!r && r.outcome_unknown === true).length;

// D74(c): the readiness row of a teacher with a blocking issue but no month row: no figure exists.
export const NO_MONTH_ROW_TEXT = 'No payment record for this month';

// CD-82: a CLOSE_NO_MONTH_ROW blocker line already says "no payment record", so the figure slot stays empty.
const no_row_net_text = (blockers) =>
  blockers.some((b) => b.code === 'CLOSE_NO_MONTH_ROW') ? '' : NO_MONTH_ROW_TEXT;

// D74(b): CLOSE_GRANT_MISMATCH normally names the penalty; the server may return none.
export const GRANT_MISMATCH_UNNAMED_TEXT =
  "The server did not name the penalty behind this refusal; check this teacher's penalty overrides for the month.";

// ---- tones (presentation only) ----

const TIER_TONES = {
  ERRORS: 'red',
  MUST_REVIEW: 'orange',
  NEEDS_ATTENTION: 'yellow',
  PROBABLE_ISSUES: 'blue',
  SETUP_GAPS: 'blue',
  CHANGED_AFTER_REVIEW: 'blue',
  INFO: 'gray'
};

const STATUS_TONES = {
  OPEN: 'orange',
  ACKNOWLEDGED: 'blue',
  RESOLVED: 'green',
  AUTO_CLEARED: 'gray'
};

const STATUS_LABELS = {
  OPEN: 'Open',
  ACKNOWLEDGED: 'Acknowledged',
  RESOLVED: 'Resolved',
  AUTO_CLEARED: 'Auto-cleared'
};

const OUTCOME_TONES = {
  CLOSED: 'green',
  REOPENED: 'green',
  REFUSED: 'red',
  SKIPPED: 'gray'
};

const OUTCOME_LABELS = {
  CLOSED: 'Closed',
  REOPENED: 'Reopened',
  REFUSED: 'Refused',
  SKIPPED: 'Skipped'
};

// CD-99/CD-107: the wire says REFUSED, but the write may have landed, so "Refused" would be a claim.
export const OUTCOME_NOT_CONFIRMED_LABEL = 'Not confirmed';

// R5 F1/D-11 carried into R5b: a teacher whose month could not be computed, or whose ERRORS are open,
// must never show a final-looking rupee figure — these two blockers say exactly that.
const TRUST_BREAKING_BLOCKERS = ['CLOSE_COMPUTE_FAILED', 'CLOSE_BLOCKED_ERRORS'];

export const trust_broken = (blockers) =>
  list_of(blockers).some((b) => b && TRUST_BREAKING_BLOCKERS.indexOf(b.code) !== -1);

// D63: needs_review (needs_recalc OR compute_error, the R5 predicate) breaks trust on its own — a
// mid-recompute or errored teacher-month can carry NO blocker and would otherwise render a settled figure.
export const amount_trust_broken = (needs_review, blockers) =>
  needs_review === true || trust_broken(blockers);

// A rupee figure is shown only when it can be trusted; otherwise the row says so.
// A-23: the amounts shown are the payable, to the paisa.
export const trusted_amount_text = (amount, broken) =>
  broken ? NEEDS_REVIEW_TEXT : fmt(amount, 2);

// Ops C20: a close attempt's own outcome shows the computed figure WITH the needs-review tag when one
// was actually computed (e.g. CLOSE_CARRY_FORWARD_FAILED — the write failed after computing a real
// number). A trust-breaking blocker (CLOSE_COMPUTE_FAILED / CLOSE_BLOCKED_ERRORS, BL14/D74(b)) means no
// figure ever existed, so it stays tag-only — unlike trusted_amount_text (the readiness list).
export const close_net_text = (amount, needs_review, blockers) => {
  if (trust_broken(blockers)) return NEEDS_REVIEW_TEXT;
  return needs_review === true ? `${fmt(amount, 2)} ${NEEDS_REVIEW_TEXT}` : fmt(amount, 2);
};

// D75: an ERRORS issue Ops has resolved or acknowledged is a decision on record, not a live error.
const ERROR_DECIDED_LABELS = { RESOLVED: 'Error resolved', ACKNOWLEDGED: 'Error acknowledged' };

const status_label_of = (tier, status) =>
  (tier === 'ERRORS' && ERROR_DECIDED_LABELS[status]) || STATUS_LABELS[status] || status;

// The Ops person by name; an older server without resolved_by_name falls back to resolved_by.
const resolver_of = (r) => r.resolved_by_name || r.resolved_by || '';

// ---- /needs_attention/summary ----

export const READINESS_DEGRADED_TEXT = 'Readiness could not be loaded';

// CD-84: with no open teacher-month ("All N closed", "Nothing to close") there is nothing to warn about.
// Only a sent 0 counts: a header missing the field is not proof that nothing is open.
const readiness_tone = (ready, teachers_open) => {
  if (ready) return 'ok';
  return teachers_open === 0 ? 'info' : 'warn';
};

// HM5: the verdict (ready + headline) is the server's. A degraded header is a failed read, not a
// month with zero teachers, so it carries no counts and is never ready.
export const readiness_view = (readiness) => {
  const r = readiness || {};
  const degraded = r.degraded === true;
  const ready = r.ready === true && !degraded;
  const closed = num(r.teachers_closed);
  const total = num(r.teachers_total);
  if (degraded) {
    return {
      ...readiness_view(null),
      degraded,
      headline: text(r.headline, READINESS_DEGRADED_TEXT),
      tone: 'error',
      teachers_text: ''
    };
  }
  return {
    ready,
    degraded,
    headline: text(r.headline, ready ? 'Ready to close' : 'Not ready to close'),
    errors_open: num(r.errors_open),
    must_review_open: num(r.must_review_open),
    blocking_teachers: num(r.blocking_teachers),
    teachers_total: total,
    teachers_closed: closed,
    teachers_open: num(r.teachers_open),
    teachers_ready: num(r.teachers_ready),
    requires_override: r.requires_override === true,
    tone: readiness_tone(ready, r.teachers_open),
    teachers_text: `${fmt_count(closed)} of ${fmt_count(total)} teacher-months closed · ${fmt_count(
      num(r.teachers_ready)
    )} ready · ${fmt_count(num(r.blocking_teachers))} blocked`,
    override_text: r.requires_override
      ? 'Some open teachers can only be closed with a second (override) reason.'
      : ''
  };
};

export const tier_row_view = (tier_row) => {
  const t = tier_row || {};
  const tier = text(t.tier, '');
  const open = num(t.open_count);
  const acknowledged = num(t.acknowledged_count);
  const resolved = num(t.resolved_count);
  const auto_cleared = num(t.auto_cleared_count);
  const outstanding = open + acknowledged;
  // D80: the server groups these by REGISTRY tier, the same resolution as the tier filter, so the
  // count and the list it drills into agree.
  const decided = acknowledged + resolved;
  return {
    key: tier || 'tier',
    tier,
    label: text(t.label, TIER_LABELS[tier] || tier),
    blocks_close: t.blocks_close === true,
    open_count: open,
    acknowledged_count: acknowledged,
    resolved_count: resolved,
    auto_cleared_count: auto_cleared,
    decided_count: decided,
    // teachers / sessions / ₹ are counted over OPEN + ACKNOWLEDGED only (contract §4.1).
    outstanding,
    teachers: num(t.teachers),
    sessions: num(t.sessions),
    amount_text: fmt(t.amount_at_stake),
    status_text: `${fmt_count(open)} open · ${fmt_count(acknowledged)} acknowledged · ${fmt_count(
      resolved
    )} resolved · ${fmt_count(auto_cleared)} auto-cleared`,
    tone: TIER_TONES[tier] || 'gray',
    is_clear: outstanding === 0,
    // A tier with nothing at all (not even history) has nothing to drill into.
    can_drill: outstanding + resolved + auto_cleared > 0
  };
};

export const READINESS_LOAD_FAILED_TEXT = 'Could not load the readiness of this month.';
export const TIERS_EMPTY_TEXT = 'No tiers were returned for this month.';
export const TIERS_LOAD_FAILED_TEXT = 'Could not load the tiers for this month.';
export const DECISIONS_EMPTY_TEXT = 'No Ops decisions for this month yet.';
export const DECISIONS_LOAD_FAILED_TEXT = 'Could not load Ops decisions.';

// The action is the Ops verb; its label is the status it left, so the panel reads like the issue list.
const ACTION_STATUS = { ACKNOWLEDGE: 'ACKNOWLEDGED', RESOLVE: 'RESOLVED' };

export const ops_decision_view = (row) => {
  const r = row || {};
  const tier = text(r.tier, '');
  const action = text(r.action, '');
  const status = ACTION_STATUS[action] || '';
  return {
    key: `decision-${num(r.issue_id)}`,
    issue_id: num(r.issue_id),
    issue_code: text(r.issue_code, ''),
    tier,
    tier_label: TIER_LABELS[tier] || tier,
    // Every row here is decided, so an ERRORS row is never the live-error red (D75).
    tier_tone: tier === 'ERRORS' ? 'gray' : TIER_TONES[tier] || 'gray',
    label: text(r.label, text(r.issue_code, '')),
    action,
    action_label: status ? status_label_of(tier, status) : action,
    action_tone: STATUS_TONES[status] || 'gray',
    who: text(resolver_of(r)),
    // The cms_user uuid is a tooltip only.
    who_uuid: text(r.resolved_by, ''),
    // Card title reads "<status> by <Ops person>"; blank rather than "by —".
    by_text: resolver_of(r) ? `by ${resolver_of(r)}` : '',
    // resolved_at is already an IST minute, like the issue rows.
    resolved_at: text(r.resolved_at, ''),
    when_text: r.resolved_at ? `${r.resolved_at} IST` : '—',
    note: text(r.resolution_note, ''),
    teacher_uuid: text(r.teacher_uuid, ''),
    teacher_name: text(r.teacher_name),
    teacher_short: short_uuid(r.teacher_uuid)
  };
};

export const summary_view = (data) => {
  const d = data || {};
  const tiers = list_of(d.tiers).map(tier_row_view);
  // Server order is newest first; kept as sent.
  const decisions = list_of(d.recent_decisions)
    .slice(0, RECENT_DECISIONS_LIMIT)
    .map(ops_decision_view);
  // Count and list are separate reads server-side; never show fewer in total than listed.
  const decisions_total = Math.max(num(d.decisions_total), decisions.length);
  return {
    pay_month: text(d.pay_month, ''),
    month_label: month_label(d.pay_month),
    generated_at: text(d.generated_at, ''),
    shadow_banner: d.shadow_banner === true,
    // HL11: a server without the field is today's behaviour — close on, refused with CLOSE_DISABLED.
    close_enabled: d.close_enabled !== false,
    readiness: readiness_view(d.readiness),
    tiers,
    total_outstanding: tiers.reduce((acc, t) => acc + t.outstanding, 0),
    decisions,
    decisions_total,
    decisions_count_text: decisions.length
      ? `Latest ${fmt_count(decisions.length)} of ${fmt_count(decisions_total)}`
      : '',
    can_see_all_decisions: decisions_total > 0 || decisions.length > 0
  };
};

// ---- /needs_attention/issues ----

// resolved_at is already an IST minute ("2026-07-01 12:20"); AUTO_CLEARED has no human actor.
const resolution_text = (tier, status, r) => {
  const at = r.resolved_at || '';
  if (!at) return '';
  const label = status_label_of(tier, status);
  if (status === 'AUTO_CLEARED') return `${label} on ${at} IST`;
  return `${label} by ${text(resolver_of(r))} on ${at} IST`;
};

export const issue_row_view = (row) => {
  const r = row || {};
  const tier = text(r.tier, '');
  const status = text(r.status, '');
  const session_id = num(r.session_id);
  const is_error = tier === 'ERRORS';
  const is_live = status === 'OPEN';
  return {
    key: `issue-${num(r.id)}`,
    id: num(r.id),
    issue_code: text(r.issue_code, ''),
    tier,
    tier_label: TIER_LABELS[tier] || tier,
    // A decided error must not wear the live-error red.
    tier_tone: is_error && !is_live ? 'gray' : TIER_TONES[tier] || 'gray',
    is_live_error: is_error && is_live,
    label: text(r.label, text(r.issue_code, '')),
    flag_reason: text(r.flag_reason, ''),
    suggested_action: text(r.suggested_action, ''),
    blocks_close: r.blocks_close === true,
    status,
    status_label: status_label_of(tier, status),
    status_tone: STATUS_TONES[status] || 'gray',
    teacher_uuid: text(r.teacher_uuid, ''),
    teacher_name: text(r.teacher_name),
    teacher_slug: text(r.teacher_slug, ''),
    teacher_short: short_uuid(r.teacher_uuid),
    pay_month: text(r.pay_month, ''),
    session_id,
    session_uuid: text(r.session_uuid, ''),
    // session_id 0 is the contract's month-level marker (§2.2): it is a teacher-month issue, not a session one.
    scope_label: session_id === 0 ? 'Month-level' : short_uuid(r.session_uuid) || `#${session_id}`,
    // The merged Session column: its link text (date/time, "Month" when there is no session to link to)
    // and the muted line under it — the student's name; nothing (not the uuid) until BE-1 sends it.
    session_start_text: session_id === 0 ? 'Month' : text(r.session_start_ist, '—'),
    student_name: text(r.student_name, ''),
    amount_text: stake_text(r.amount_at_stake, r.issue_code),
    first_seen: text(r.first_seen_at),
    last_seen: text(r.last_seen_at),
    resolution_text: resolution_text(tier, status, r),
    resolver_uuid: text(r.resolved_by, ''),
    resolution_note: text(r.resolution_note, ''),
    acknowledgeable: is_acknowledgeable(r),
    selectable: is_issue_live(r)
  };
};

export const ISSUES_LOAD_FAILED_TEXT = 'Could not load the issues.';

// The default filter (no status sent = OPEN + ACKNOWLEDGED) hides decided issues; say where they are.
export const issues_empty_text = (status) =>
  status
    ? 'No issues for this tier, month and status filter.'
    : 'No open or acknowledged issues. Resolved and auto-cleared issues are under the Status filter.';

export const issues_view = (data) => {
  const d = data || {};
  return {
    pay_month: text(d.pay_month, ''),
    rows: list_of(d.rows).map(issue_row_view),
    count: num(d.count),
    total_count: num(d.total_count),
    amount_text: fmt(d.amount_at_stake),
    limit: num(d.limit),
    offset: num(d.offset),
    sort: text(d.sort, ''),
    dir: text(d.dir, '')
  };
};

// ---- Daily report: one line's individual issues (the same /needs_attention/issues endpoint, filtered
// by issue_code, sort=session_time). A digest window can span two pay_months; "Show more" exhausts one
// month's pages before moving to the next, in order — not a merged cross-month sort. ----

export const DAILY_ISSUES_PAGE_SIZE = 50;

// "Cleared" is RESOLVED + AUTO_CLEARED (needs_attention_db.go sqlDailyCleared); New/Threshold read as
// currently outstanding (the list's own default: no status sent = OPEN + ACKNOWLEDGED).
export const daily_issue_status = (section_key) =>
  section_key === 'cleared_issues' ? 'RESOLVED,AUTO_CLEARED' : '';

export const daily_issue_list_init = (pay_months) => {
  const months = list_of(pay_months);
  return { months, month_index: 0, offset: 0, rows: [], done: months.length === 0 };
};

// Pure: the next page to fetch, or null once every month in the line is exhausted.
export const daily_issue_next_request = (state) => {
  if (state.done || state.month_index >= state.months.length) return null;
  return { pay_month: state.months[state.month_index], offset: state.offset };
};

// Pure: folds one fetched page (an issues_view) into the accumulated state; once a month's rows are
// exhausted, the following entry in state.months takes over.
export const daily_issue_page_applied = (state, request, page) => {
  const month_done = request.offset + page.rows.length >= page.total_count;
  const month_index = month_done ? state.month_index + 1 : state.month_index;
  return {
    ...state,
    rows: state.rows.concat(page.rows),
    month_index,
    offset: month_done ? 0 : request.offset + page.rows.length,
    done: month_index >= state.months.length
  };
};

// ---- /needs_attention/issue_action ----

// Stand-ins for a skip the server sent without a message; its own message is the specific one.
export const ISSUE_ACTION_SKIP_TEXT = {
  ISSUE_NOT_FOUND: 'This issue no longer exists.',
  ISSUE_ALREADY_CLOSED: 'Already decided; this action changes nothing.',
  ISSUE_TIER_NOT_ACKNOWLEDGEABLE:
    'An error cannot be acknowledged; fix it or resolve it with a reason.',
  ISSUE_AUTO_CLEARED: 'The system already cleared this issue.'
};

const SKIP_REASONS = {
  ACKNOWLEDGE:
    'an error is fixed, never acknowledged, and a resolved issue is never actioned again',
  RESOLVE: 'a resolved or auto-cleared issue is never actioned again'
};

// Said before the request, naming why the action skips the rows it cannot apply to.
export const issue_skip_text = (action, skipped_rows) => {
  const rows = list_of(skipped_rows);
  if (!rows.length) return '';
  const reason = SKIP_REASONS[action === ACTION_ACKNOWLEDGE ? 'ACKNOWLEDGE' : 'RESOLVE'];
  return `${fmt_count(rows.length)} of the selected issues will be skipped: ${reason}.`;
};

// CD-62, worded for the dialog it shows in (the list is behind it); the server skips a decided row.
export const ISSUE_ACTION_DROPPED_TEXT =
  'The connection dropped — it may or may not have been saved. The list behind is refreshed; it is safe to try again — an issue already decided is skipped.';

export const issue_action_view = (data) => {
  const d = data || {};
  const applied = num(d.applied);
  const skipped = num(d.skipped);
  return {
    applied,
    skipped,
    headline: `${fmt_count(applied)} updated · ${fmt_count(skipped)} skipped`,
    results: list_of(d.results).map((r) => {
      const ok = r && r.applied === true;
      const code = text(r && r.code, '');
      const status_label = STATUS_LABELS[(r && r.status) || ''] || text(r && r.status, '');
      return {
        key: `action-${num(r && r.issue_id)}`,
        issue_id: num(r && r.issue_id),
        applied: ok,
        status: text(r && r.status, ''),
        status_label,
        code,
        message: text(r && r.message, ''),
        text: ok
          ? `Now ${status_label.toLowerCase()}`
          : text(r && r.message, ISSUE_ACTION_SKIP_TEXT[code] || 'Skipped.'),
        // The stable code stays reachable for support, off the visible line.
        title: code,
        tone: ok ? 'green' : 'orange'
      };
    })
  };
};

// ---- /needs_attention/readiness ----

export const blocker_view = (blocker) => {
  const b = blocker || {};
  const code = text(b.code, '');
  const overridable = is_blocker_overridable(b);
  return {
    key: code || 'blocker',
    code,
    message: text(b.message, ''),
    overridable,
    // how_to_fix is the backend's single wording per code: the close dialog and this list cannot drift.
    how_to_fix: text(b.how_to_fix, ''),
    // D74(b): the server's own message, verbatim — never a label mapped from the code (an unknown code
    // renders the same way). The code stands in only when the message is missing.
    line: [text(b.message, code), text(b.how_to_fix, '')].filter(Boolean).join(' — '),
    tone: overridable ? 'orange' : 'red'
  };
};

const sessions_text = (no_row, sessions) => {
  if (no_row) return '';
  return sessions === 0 ? 'Empty month' : `${fmt_count(sessions)} sessions`;
};

export const teacher_readiness_view = (row) => {
  const r = row || {};
  const blockers = list_of(r.blockers).map(blocker_view);
  const broken = amount_trust_broken(r.needs_review, blockers);
  const sessions = num(r.session_count);
  const no_row = is_no_month_row(r);
  return {
    key: text(r.teacher_uuid, 'teacher'),
    teacher_uuid: text(r.teacher_uuid, ''),
    teacher_name: text(r.teacher_name),
    teacher_short: short_uuid(r.teacher_uuid),
    lifecycle: text(r.lifecycle, ''),
    // D74(c): its ₹0 is not a computed figure (D63), so the row says there is no record instead.
    no_month_row: no_row,
    net_text: no_row ? no_row_net_text(blockers) : trusted_amount_text(r.payable_amount, broken),
    net_tone: broken || no_row ? 'tp-neg' : 'tp-strong',
    needs_review: r.needs_review === true,
    trust_broken: broken || no_row,
    closable: r.closable === true,
    selectable: is_selectable_for_close(r),
    blockers,
    session_count: sessions,
    // An empty month still closes like any other (§5.6); D78 means it never holds up a later month.
    is_empty_month: !no_row && sessions === 0,
    sessions_text: sessions_text(no_row, sessions),
    hard_blocked: blockers.some((b) => !b.overridable),
    needs_override: blockers.some((b) => b.overridable)
  };
};

export const READINESS_LIST_LOAD_FAILED_TEXT = 'Could not load the teachers for this month.';

// Where the next page starts and whether there is one. Without total_count (an older server) a full
// page is the only sign that more rows exist.
const paging_of = (has_total, total_count, count, limit, next_offset) => ({
  next_offset,
  has_more: has_total ? next_offset < total_count : limit > 0 && count >= limit
});

export const readiness_list_view = (data) => {
  const d = data || {};
  const rows = list_of(d.rows).map(teacher_readiness_view);
  const has_total = d.total_count !== undefined && d.total_count !== null;
  const count = num(d.count);
  const limit = num(d.limit);
  const offset = num(d.offset);
  return {
    pay_month: text(d.pay_month, ''),
    state: text(d.state, ''),
    rows,
    count,
    has_total,
    total_count: has_total ? num(d.total_count) : rows.length,
    limit,
    offset,
    ...paging_of(has_total, num(d.total_count), count, limit, offset + count),
    close_enabled: d.close_enabled !== false,
    readiness: readiness_view(d.readiness)
  };
};

// "Load more": a row that slid across the page boundary between two reads is listed once; the counts
// and the readiness header come from the newer page.
export const append_readiness_page = (prev, next) => {
  const seen = prev.rows.map((r) => r.teacher_uuid);
  const rows = prev.rows.concat(next.rows.filter((r) => seen.indexOf(r.teacher_uuid) === -1));
  return {
    ...next,
    rows,
    total_count: next.has_total ? next.total_count : rows.length,
    ...paging_of(next.has_total, next.total_count, next.count, next.limit, next.next_offset)
  };
};

export const readiness_showing_text = (list) =>
  `Showing ${fmt_count(list.rows.length)} of ${fmt_count(
    list.has_total ? list.total_count : list.rows.length
  )}`;

// ---- /month/close ----

export const grant_mismatch_view = (item, idx = 0) => {
  const g = item || {};
  return {
    key: `${text(g.penalty_uuid, 'penalty')}-${idx}`,
    session_uuid: text(g.session_uuid, ''),
    session_short: short_uuid(g.session_uuid),
    penalty_uuid: text(g.penalty_uuid, ''),
    day_ist: text(g.day_ist, ''),
    penalty_type: text(g.penalty_type, ''),
    // HM2-M2: 2 decimals — a close refusal must show the exact amount, not the L1 whole-rupee rounding.
    amount_text: fmt(g.amount, 2),
    is_overridden: g.is_overridden === true,
    how_to_fix: text(g.how_to_fix, ''),
    // D33/HM2-M2: name the penalty in plain language and the session by day + short id, not the raw
    // enum code and a 36-char uuid — Ops reads this line, it is not a debug trace.
    line: `${text(g.day_ist)} · ${type_label(g.penalty_type)} ${fmt(g.amount, 2)} · session ${
      short_uuid(g.session_uuid) || text(g.session_uuid)
    }`
  };
};

// `precheck`: the row is a pre-check refusal (requires_override), not the outcome of a close attempt (CD-91).
export const close_result_view = (result, precheck = false) => {
  const r = result || {};
  const blockers = list_of(r.blockers).map(blocker_view);
  const outcome = text(r.outcome, '');
  const broken = amount_trust_broken(r.needs_review, blockers);
  // CD-47: a close of a teacher with no month row writes nothing; its ₹0 is no figure (as D74(c)).
  const no_row = is_no_month_row(r);
  // CD-99: whether a figure was frozen is unknown, so none is shown; the blocker line says why.
  const unknown = is_outcome_unknown(r);
  let net_text = close_net_text(r.payable_amount, r.needs_review === true, blockers);
  if (no_row) net_text = no_row_net_text(blockers);
  if (unknown) net_text = '';
  return {
    key: text(r.teacher_uuid, 'teacher'),
    teacher_uuid: text(r.teacher_uuid, ''),
    teacher_name: text(r.teacher_name),
    outcome,
    outcome_unknown: unknown,
    outcome_label: unknown ? OUTCOME_NOT_CONFIRMED_LABEL : OUTCOME_LABELS[outcome] || outcome,
    outcome_tone: unknown ? 'orange' : OUTCOME_TONES[outcome] || 'gray',
    lifecycle: text(r.lifecycle, ''),
    grant_written: r.grant_written === true,
    grant_text: r.grant_written === true ? 'Free-pass grant written' : '',
    net_text,
    net_tone: broken || no_row ? 'tp-neg' : 'tp-strong',
    needs_review: r.needs_review === true,
    trust_broken: broken || no_row,
    override_used: r.override_used === true,
    precheck: precheck === true,
    blockers,
    grant_mismatch: list_of(r.grant_mismatch).map(grant_mismatch_view),
    grant_mismatch_unnamed_text:
      blockers.some((b) => b.code === 'CLOSE_GRANT_MISMATCH') && !list_of(r.grant_mismatch).length
        ? GRANT_MISMATCH_UNNAMED_TEXT
        : ''
  };
};

// "N not confirmed" is taken out of the server's refused count and shown only when there is one.
const close_totals = (c) => {
  const unknown = not_confirmed_count(c.results);
  return [
    `${fmt_count(num(c.closed))} closed`,
    `${fmt_count(Math.max(num(c.refused) - unknown, 0))} refused`,
    unknown > 0 ? `${fmt_count(unknown)} not confirmed` : '',
    `${fmt_count(num(c.skipped))} skipped`
  ]
    .filter(Boolean)
    .join(' · ');
};

const OVERRIDE_NEEDED_TEXT =
  'The refused teachers marked Overridable need an override reason; the others cannot be closed with one.';

// A close envelope as one sentence. A requires_override answer wrote nothing (D33), so it says so and
// points at the Overridable pills (R7RH-F4).
export const close_headline = (counts) => {
  const c = counts || {};
  const totals = close_totals(c);
  if (c.requires_override === true) {
    return `${num(c.closed) === 0 ? 'Nothing was closed.' : `${totals}.`} ${OVERRIDE_NEEDED_TEXT}`;
  }
  return totals;
};

export const close_response_view = (data) => {
  const d = data || {};
  const closed = num(d.closed);
  const refused = num(d.refused);
  const skipped = num(d.skipped);
  const requires_override = d.requires_override === true;
  const results = list_of(d.results).map((r) => close_result_view(r, requires_override));
  return {
    pay_month: text(d.pay_month, ''),
    closed,
    refused,
    skipped,
    requires_override,
    results,
    readiness: readiness_view(d.readiness),
    headline: close_headline({ closed, refused, skipped, requires_override, results })
  };
};

// HL2: any refusal is a warning; an answer that closed nothing and refused nothing is neutral.
export const close_result_tone = (view) => {
  const m = view || {};
  if (m.requires_override === true || num(m.refused) > 0) return 'warn';
  return num(m.closed) > 0 ? 'ok' : 'info';
};

// ---- /month/reopen ----

// D84: while this month is open again, no later month with sessions or money can close.
export const REOPENED_HEADLINE =
  'Month reopened. No later month with sessions or money can close until this one is closed again.';
// The server's refusal is the blocker line below; the headline does not repeat it (HL1).
export const REOPEN_REFUSED_HEADLINE = 'This teacher-month was not reopened.';
// CD-107: the reopen may have been saved; the server's blocker line says so and how to check.
export const REOPEN_UNKNOWN_HEADLINE = 'This reopen was not confirmed.';
// A retry is safe: a month that did reopen is no longer CLOSED, so it is refused, never reopened twice.
export const REOPEN_DROPPED_TEXT =
  'The connection dropped before the answer came back, so the reopen may or may not have happened. The list below is refreshed — check it before reopening again.';

const reopen_headline = (outcome, unknown) => {
  if (outcome === 'REOPENED') return REOPENED_HEADLINE;
  return unknown ? REOPEN_UNKNOWN_HEADLINE : REOPEN_REFUSED_HEADLINE;
};

export const reopen_view = (data) => {
  const d = data || {};
  const outcome = text(d.outcome, '');
  const blockers = list_of(d.blockers).map(blocker_view);
  const regated = list_of(d.regated_months).filter(Boolean);
  const unknown = is_outcome_unknown(d);
  const notes = [];
  if (d.grant_reversed === true) notes.push('The free-pass grant for this month was reversed.');
  if (regated.length) notes.push(`Also re-checked: ${regated.map(month_label).join(', ')}.`);
  return {
    pay_month: text(d.pay_month, ''),
    teacher_uuid: text(d.teacher_uuid, ''),
    outcome,
    outcome_unknown: unknown,
    outcome_label: unknown ? OUTCOME_NOT_CONFIRMED_LABEL : OUTCOME_LABELS[outcome] || outcome,
    outcome_tone: unknown ? 'orange' : OUTCOME_TONES[outcome] || 'gray',
    lifecycle: text(d.lifecycle, ''),
    grant_reversed: d.grant_reversed === true,
    regated_months: regated,
    reopen_count: num(d.reopen_count),
    blockers,
    notes,
    headline: reopen_headline(outcome, unknown)
  };
};

// ---- /needs_attention/daily ----

export const daily_line_view = (line) => {
  const l = line || {};
  const tier = text(l.tier, '');
  const months = list_of(l.pay_months).filter(Boolean);
  return {
    key: `${text(l.issue_code, 'issue')}-${tier}`,
    issue_code: text(l.issue_code, ''),
    tier,
    tier_label: TIER_LABELS[tier] || tier,
    tier_tone: TIER_TONES[tier] || 'gray',
    label: text(l.label, text(l.issue_code, '')),
    count: num(l.count),
    teachers: num(l.teachers),
    // null (not 0) for a stake_rule NONE code, so the shared sort_rows blank rule sorts it last on
    // ₹ at stake rather than tying every no-stake line at the bottom of the real zeros.
    amount_at_stake: has_no_stake(l.issue_code) ? null : num(l.amount_at_stake),
    amount_text: stake_text(l.amount_at_stake, l.issue_code),
    pay_months: months,
    months_text: months.length ? months.map(month_label).join(', ') : '—'
  };
};

const JOB_EVENT_TONES = { outage: 'red', recovered: 'green' };
const JOB_EVENT_LABELS = { outage: 'Outage', recovered: 'Recovered' };

export const daily_job_event_view = (event, idx = 0) => {
  const e = event || {};
  const kind = text(e.event, '');
  const step = text(e.step, '');
  return {
    key: `${text(e.job, 'job')}-${kind}-${idx}`,
    job: text(e.job, ''),
    event: kind,
    event_label: JOB_EVENT_LABELS[kind] || kind,
    at: text(e.at),
    step,
    tone: JOB_EVENT_TONES[kind] || 'gray',
    text: `${text(e.job)} ${kind === 'outage' ? 'failed' : kind}${step ? ` at ${step}` : ''}`
  };
};

export const DAILY_LOAD_FAILED_TEXT = 'Could not load the daily report for this day.';

// `today` is injected (never read from the clock inside) so the link whitelist stays unit-testable.
export const daily_view = (data, today = ist_day()) => {
  const d = data || {};
  const totals = d.totals || {};
  return {
    day: text(d.day, ''),
    from: text(d.from, ''),
    to: text(d.to, ''),
    window_text: `${text(d.from, '')} → ${text(d.to, '')} IST`,
    sections: [
      {
        key: 'new_issues',
        title: 'New since the previous post',
        empty_text: 'No new issues in this window.',
        lines: list_of(d.new_issues).map(daily_line_view)
      },
      {
        key: 'cleared_issues',
        title: 'Resolved or auto-cleared',
        empty_text: 'Nothing was cleared in this window.',
        lines: list_of(d.cleared_issues).map(daily_line_view)
      },
      {
        key: 'threshold_crossings',
        title: 'Threshold crossings',
        empty_text: 'No teacher crossed an anomaly threshold.',
        lines: list_of(d.threshold_crossings).map(daily_line_view)
      }
    ],
    job_events: list_of(d.job_events).map(daily_job_event_view),
    totals: {
      new: num(totals.new),
      cleared: num(totals.cleared),
      crossings: num(totals.crossings),
      job_events: num(totals.job_events)
    },
    totals_text: `${fmt_count(num(totals.new))} new · ${fmt_count(
      num(totals.cleared)
    )} cleared · ${fmt_count(num(totals.crossings))} crossings · ${fmt_count(
      num(totals.job_events)
    )} job events`,
    all_clear: d.all_clear === true,
    monthly_link: text(d.monthly_link, ''),
    // The monthly link is followed in place (same page, monthly view) instead of a reload.
    monthly_target: parse_link_target(d.monthly_link, today)
  };
};
